import { useAuthState } from '@auth/hooks';
import { CommunityDialog } from '@communities/community/components/CommunityDialog';
import type { Community } from '@communities/community/domain';
import { useCommunities } from '@communities/community/hooks';
import { CommunityGroupLoadingState, CommunityGroupAuthRequired } from '@communities/community-groups/components';
import { useCommunityGroupDetail } from '@communities/community-groups/hooks';
import * as styles from '@communities/community-groups/pages/CommunityGroupDetailPage.css';
import { EntityType } from '@communities/content-management/domain';
import { TabNavigation } from '@communities/content-management/pages';
import { routes } from '@communities/routes';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { ErrorDisplay, HeroBackground } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CommunityGroupHeaderSection } from './sections/CommunityGroupHeaderSection';

/**
 * Community Group Detail Page Component
 *
 * Displays detailed information about a community group and its communities
 */

// eslint-disable-next-line complexity
function CommunityGroupDetailPageComponent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthState();
  const { data: communityGroup, isCommunityGroupAdmin, loading, errors } = useCommunityGroupDetail();
  const { data: communities, loading: _communitiesLoading, errors: _communitiesErrors } = useCommunities();

  // Feature flag for community group objects
  const isCommunityGroupObjectsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.COMMUNITY_GROUP_OBJECTS);

  // Dialog states
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [isAddCommunityDialogOpen, setIsAddCommunityDialogOpen] = useState(false);

  // Check for action query parameter to auto-open create community dialog
  useEffect(() => {
    const action = searchParams.get('action');

    if (action === 'create-community' && !isAddCommunityDialogOpen) {
      setIsAddCommunityDialogOpen(true);
      // Remove the query parameter after opening the dialog
      searchParams.delete('action');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isAddCommunityDialogOpen]);

  const handleSelectCommunity = (community: Community) => {
    navigate(routes.communities.detail(community.id));
  };

  // Authentication check
  if (!isAuthenticated) {
    return <CommunityGroupAuthRequired />;
  }

  // Show error state when errors exist and no data
  if (errors && !communityGroup && !loading) {
    return (
      <ErrorDisplay
        isOpen
        correlationId={errors[0]?.correlationId}
        message={formatErrorMessages(errors)}
        title="Unable to Load Community Group"
        onBack={() => navigate(routes.communityGroups.root)}
        onClose={() => navigate(routes.communityGroups.root)}
      />
    );
  }

  return (
    <div>
      <HeroBackground imageUrl={undefined} />

      {loading ? (
        <CommunityGroupLoadingState />
      ) : !communityGroup ? null : (
        <>
          {/* Header Section with Background Image */}
          <CommunityGroupHeaderSection
            communities={communities}
            communityGroup={communityGroup}
            isCommunityGroupAdmin={isCommunityGroupAdmin}
            onAddCommunity={() => setIsAddCommunityDialogOpen(true)}
            onSelectCommunity={handleSelectCommunity}
          />

          {/* Content Section with Solid Background */}
          {isCommunityGroupObjectsEnabled && (
            <div className={styles.contentSection}>
              <div className={styles.tabContent}>
                <TabNavigation entityId={communityGroup.id} entityType={EntityType.COMMUNITY_GROUP} isEntityAdmin={isCommunityGroupAdmin} />
              </div>
            </div>
          )}
        </>
      )}

      {/* Community Dialog */}
      {communityGroup && <CommunityDialog groupId={communityGroup.id} isOpen={isAddCommunityDialogOpen} onClose={() => setIsAddCommunityDialogOpen(false)} />}

      {/* Error Display - Strategy-based rendering */}
      {errors && <ErrorDisplay isOpen={showErrorDialog} message={formatErrorMessages(errors)} title="Error" onClose={() => setShowErrorDialog(false)} />}
    </div>
  );
}

export const CommunityGroupDetailPage = withAppInsightsTracking(CommunityGroupDetailPageComponent, 'CommunityGroupDetailPage');
